import { defaultLoginCopy, SIGN_IN_CONSENT } from "@loot-os/api-contract";
import type { LoginContent } from "@loot-os/design-system/templates/login/loginContent";
import { toRichText } from "@loot-os/design-system/templates/shared/content";
import { useMutation } from "@tanstack/react-query";
import { useSearch } from "@tanstack/react-router";
import { useEffect, useState } from "react";

import { track } from "../../../lib/analytics.js";
import { deviceSessionHeaders, prepareDeviceSession } from "../../../lib/deviceSession.js";
import { useTenant } from "../../../lib/tenant.js";

/**
 * The sign-in page's content: one email form that both signs up and signs in by emailing a magic
 * link through the BFF, then where the link went. `?error=invalid_link` is the BFF's callback
 * sending the shopper back from a link that expired or was already used. The words are the store's
 * default copy (`defaultLoginCopy`); the consent line (`SIGN_IN_CONSENT`), the expired-link notice
 * and a refusal are the platform's.
 */
export function useLoginContent(): LoginContent {
  const { config } = useTenant();
  const copy = defaultLoginCopy({ storeName: config.name });
  const search = useSearch({ from: "/login" });
  const linkError = search.error === "invalid_link";
  const [email, setEmail] = useState("");

  // Stores with fraud screening on load the SEON agent early, so the device session is ready
  // by the time the shopper submits; the request never waits on it past a short budget.
  const screening = config.fraudScreening;
  useEffect(() => {
    if (screening) prepareDeviceSession();
  }, [screening]);

  // The BFF answers 202 whether or not the account exists, so success here
  // deliberately reveals nothing about the email.
  const magicLink = useMutation({
    mutationFn: async (to: string) => {
      const res = await fetch("/api/auth/magic-link", {
        method: "POST",
        headers: {
          "content-type": "application/json",
          ...(await deviceSessionHeaders(screening)),
        },
        body: JSON.stringify({ email: to }),
      });
      if (!res.ok) throw new Error("could not send the sign-in link");
    },
    onSuccess: () => track("sign_in_requested", {}),
  });
  const sent = magicLink.isSuccess;
  const error = magicLink.error
    ? magicLink.error instanceof Error
      ? magicLink.error.message
      : "something went wrong"
    : null;

  return {
    panel: {
      title: copy.title,
      description: copy.description,
      notice:
        linkError && !sent ? "That sign-in link is invalid or expired — request a new one." : null,
      error,
      step: sent
        ? {
            kind: "sent",
            message: { before: copy.sent.before, email, after: copy.sent.after },
            reset: { label: copy.sent.reset, onReset: () => magicLink.reset() },
          }
        : {
            kind: "form",
            email: { ...copy.email, value: email, onChange: setEmail },
            submit: { ...copy.submit, busy: magicLink.isPending },
            onSubmit: () => magicLink.mutate(email),
            // One form signs up and signs in, so consent is a line under it rather than a
            // checkbox every returning shopper would have to tick.
            consent: toRichText(SIGN_IN_CONSENT),
          },
    },
  };
}
